import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, orderBy, doc, setDoc, deleteDoc, updateDoc, increment, limit, serverTimestamp } from 'firebase/firestore';
import { onSnapshot } from '../utils/firestoreListen';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import ArtworkReactions from './ArtworkReactions';
import { getReactionArtworkId } from '../utils/reactionArtworkId';
import { useLikeStateCache } from '../hooks/useLikeStateCache';
import { PAGING } from '../config/paging';
import { trackEvent, logError } from '../utils/observability';

const PublicWallCarousel = ({ activityId, onSelect }) => {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);
  const { isLiked, ensureLoaded, setLikedOptimistic } = useLikeStateCache(user?.uid);

  useEffect(() => {
    if (!activityId || !user) return;

    // Query public submissions for this activity
    const submissionsQuery = query(
      collection(db, 'submissions'),
      where('activityId', '==', activityId),
      where('isPublic', '==', true),
      limit(PAGING.PUBLIC_SUBMISSIONS_PER_ACTIVITY_LIMIT) // cap to latest N public submissions per activity
    );

    const unsubscribe = onSnapshot(
      submissionsQuery,
      (snapshot) => {
        const submissionsData = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => {
            const at = a.createdAt?.toMillis?.() || 0;
            const bt = b.createdAt?.toMillis?.() || 0;
            return bt - at; // desc
          });
        setSubmissions(submissionsData);
        if (onSelect && submissionsData.length > 0) {
          onSelect(submissionsData[0]);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching submissions:', error);
        setError('Failed to load submissions');
        setLoading(false);
      },
      { purpose: 'PublicWallCarousel-submissions', component: 'PublicWallCarousel', essential: true }
    );

    return () => unsubscribe();
  }, [activityId, user]);

  // Lazily resolve like state for the first N submissions on initial load,
  // and for newly focused submissions as the user navigates.
  useEffect(() => {
    if (!user || submissions.length === 0) return;

    // Eagerly load like state for the first few items
    const initialCount = Math.min(PAGING.INITIAL_LIKE_BATCH, submissions.length);
    for (let i = 0; i < initialCount; i += 1) {
      const submission = submissions[i];
      if (submission?.id) {
        ensureLoaded(submission.id);
      }
    }

    // Also ensure the currently focused item is loaded (covers navigation beyond the initial batch)
    const current = submissions[currentIndex];
    if (current?.id) {
      ensureLoaded(current.id);
    }
  }, [user, submissions, currentIndex, ensureLoaded]);

  const handleLike = async (submissionId, isLiked) => {
    if (!user) return;

    try {
      const likeRef = doc(db, 'submissions', submissionId, 'likes', user.uid);
      const submissionRef = doc(db, 'submissions', submissionId);
      
      if (isLiked) {
        // Remove like
        await deleteDoc(likeRef);
        // Decrement like counter atomically (if field missing, Firestore treats it as 0)
        await updateDoc(submissionRef, { likeCount: increment(-1) }).catch(() => {});
        // Optimistically update local cache
        setLikedOptimistic(submissionId, false);
        trackEvent('submission_unliked', {
          submissionId,
          userId: user.uid,
          source: 'PublicWallCarousel',
        });
      } else {
        // Add like
        await setDoc(likeRef, {
          uid: user.uid,
          createdAt: serverTimestamp()
        });
        // Increment like counter atomically
        await updateDoc(submissionRef, { likeCount: increment(1) }).catch(() => {});
        // Optimistically update local cache
        setLikedOptimistic(submissionId, true);
        trackEvent('submission_liked', {
          submissionId,
          userId: user.uid,
          source: 'PublicWallCarousel',
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      logError(error, {
        where: 'PublicWallCarousel.handleLike',
        submissionId,
        userId: user?.uid || null,
      });
    }
  };

  const scrollToIndex = (index) => {
    if (carouselRef.current) {
      const cardWidth = 320; // Approximate card width + gap
      const scrollPosition = index * cardWidth;
      carouselRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
    setCurrentIndex(index);
  };

  const nextSlide = () => {
    const nextIndex = Math.min(currentIndex + 1, submissions.length - 1);
    scrollToIndex(nextIndex);
    if (onSelect) onSelect(submissions[nextIndex]);
  };

  const prevSlide = () => {
    const prevIndex = Math.max(currentIndex - 1, 0);
    scrollToIndex(prevIndex);
    if (onSelect) onSelect(submissions[prevIndex]);
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-amber-50/90 to-orange-50/90 backdrop-blur-sm border border-amber-200/60 rounded-2xl p-6 shadow-lg">
        <div className="">
          <div className="h-6 bg-amber-200 rounded mb-4"></div>
          <div className="h-48 bg-amber-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-to-br from-amber-50/90 to-orange-50/90 backdrop-blur-sm border border-amber-200/60 rounded-2xl p-6 shadow-lg">
        <div className="text-center text-red-600">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className="bg-gradient-to-br from-amber-50/90 to-orange-50/90 backdrop-blur-sm border border-amber-200/60 rounded-2xl p-6 shadow-lg">
        <div className="text-center">
          <div className="text-6xl mb-4">🎨</div>
          <h3 className="text-xl font-bold text-amber-800 mb-2">
            Be the First to Share!
          </h3>
          <p className="text-amber-700">
            No one has shared their artwork for this activity yet. Upload your creation to be the first!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-gradient-to-br from-amber-50/90 to-orange-50/90 backdrop-blur-sm border border-amber-200/60 rounded-2xl p-6 shadow-lg"
      data-testid="public-wall-carousel"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-amber-800">
          Other Artists' Creations
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-sm text-amber-600">
            {currentIndex + 1} of {submissions.length}
          </span>
        </div>
      </div>

      <div className="relative">
        {/* Navigation Arrows */}
        {submissions.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              disabled={currentIndex === 0}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 bg-white/90 border border-amber-200 rounded-full p-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed z-10"
            >
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextSlide}
              disabled={currentIndex === submissions.length - 1}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 bg-white/90 border border-amber-200 rounded-full p-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed z-10"
            >
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Carousel Container */}
        <div 
          ref={carouselRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {submissions.map((submission, index) => {
            const liked = isLiked(submission.id);
            
            return (
              <div
                key={submission.id}
                className="flex-shrink-0 w-80 bg-white/80 backdrop-blur-sm border border-amber-200/60 rounded-xl shadow-lg overflow-hidden"
              >
                {/* Artwork Image */}
                <div className="relative">
                  <img
                    src={submission.thumbUrl || submission.imageUrl}
                    alt="Artwork submission"
                    className="w-full h-48 object-cover"
                    loading="lazy"
                  />
                  <div className="absolute top-2 right-2">
                    <button
                      onClick={() => handleLike(submission.id, liked)}
                      className={`p-2 rounded-full shadow-lg ${
                        liked 
                          ? 'bg-red-500 text-white' 
                          : 'bg-white/90 text-gray-600'
                      }`}
                      data-testid="public-like-button"
                      >
                      <svg 
                        className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Artwork Info */}
                <div className="p-4" onClick={() => { setCurrentIndex(index); if (onSelect) onSelect(submission); }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {submission.uid?.charAt(0)?.toUpperCase() || 'A'}
                      </div>
                      <span className="ml-2 text-sm text-amber-700 font-medium" title={submission.artistName || submission.uid}>
                        {submission.artistName ? submission.artistName : `Artist ${submission.uid?.substring(0, 8) || 'Unknown'}`}
                      </span>
                    </div>
                    {/* Old heart/likes display removed; reactions handled by ArtworkReactions below */}
                  </div>
                  
                  <div className="text-xs text-amber-600">
                    {submission.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                  </div>
                  <div className="mt-2 flex justify-end">
                    <ArtworkReactions
                      artworkId={getReactionArtworkId(submission)}
                      artistId={submission.uid}
                      enableComments={false}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Dots Indicator */}
        {submissions.length > 1 && (
          <div className="flex justify-center mt-4 gap-2">
            {submissions.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className={`w-2 h-2 rounded-full ${
                  index === currentIndex ? 'bg-amber-500' : 'bg-amber-200'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicWallCarousel;
